/**
 * Hook for managing per-event notification preferences.
 *
 * Fetches the resolved preference matrix for a module and provides
 * mutation helpers for updating event preferences, module tier, and reset.
 */
import { createContext, useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAllPreferencesApiV1NotificationPreferencesGet,
  getPreferencesApiV1NotificationPreferencesModuleGet,
  resetPreferencesApiV1NotificationPreferencesModuleResetPost,
  updateEventPreferencesApiV1NotificationPreferencesModuleEventsPut,
  updateModulePreferencesApiV1NotificationPreferencesModuleModulePut,
} from '@/generated/api/notification-preferences/notification-preferences';
import type { NotificationAllPreferencesResponse } from '@/generated/api/models/notificationAllPreferencesResponse';
import type { NotificationEventPreferenceEntry } from '@/generated/api/models/notificationEventPreferenceEntry';
import type { NotificationEventPreferenceUpdateItem } from '@/generated/api/models/notificationEventPreferenceUpdateItem';
import type { NotificationModulePreferenceUpdate } from '@/generated/api/models/notificationModulePreferenceUpdate';
import type { NotificationModulePreferences } from '@/generated/api/models/notificationModulePreferences';
import type { NotificationPreferenceCategory } from '@/generated/api/models/notificationPreferenceCategory';
import type { NotificationPreferencesResponse } from '@/generated/api/models/notificationPreferencesResponse';

// ── Types ──────────────────────────────────────────────────────
//
// The generated models ARE these types — `/api/v1/notification-preferences` has been a
// typed, schema-first surface since WI c6a50d8b, so a hand-rolled copy here would be a
// duplicate that drifts the next time a schema moves (WI a925b265). What remains is a
// set of ALIASES: the call sites read better without the `Notification` prefix orval
// takes from the schema title, and an alias cannot drift because it has no field list
// of its own.

/** One event's resolved delivery preferences. */
export type EventPreference = NotificationEventPreferenceEntry;

/** A category and its events, in catalog order. */
export type CategoryWithEvents = NotificationPreferenceCategory;

/** One module's master switches and tier threshold. */
export type ModulePreferences = NotificationModulePreferences;

/** One module's full matrix — what `GET /{module}` returns. */
export type PreferencesResponse = NotificationPreferencesResponse;

/** Every catalog module's matrix, keyed by module id — what `GET /` returns. */
export type AllPreferencesResponse = NotificationAllPreferencesResponse;

/** One event's requested overrides; an omitted channel is left alone. */
export type EventPreferenceUpdate = NotificationEventPreferenceUpdateItem;

/** A module's requested master-switch / threshold changes. */
export type ModulePreferenceUpdate = NotificationModulePreferenceUpdate;

// ── API calls ──────────────────────────────────────────────────
//
// The generated FETCHERS, not just the generated types. Orval emits a function
// per operation from the same OpenAPI document the models come from, so the URL,
// the verb and the body type cannot drift from the route — which a hand-rolled
// customInstance call naming the URL and verb itself can, silently,
// in a way `tsc` never sees (`.claude/rules/rules-frontend.md` § hand-rolled
// axios calls).
//
// What is NOT taken from orval is its react-query hooks: the two reads here serve
// the same rows (one module, and the batched all-modules read), so a write to
// either has to refresh the other — which needs the one shared key prefix below,
// not two independent orval key spaces.

// ── Query keys ─────────────────────────────────────────────────

export const notificationPreferenceKeys = {
  all: ['notification-preferences'] as const,
  module: (module: string) => [...notificationPreferenceKeys.all, module] as const,
  /**
   * The all-modules read. Nested under `all` so one prefix invalidation refreshes
   * whichever of the two reads is actually mounted — see `useNotificationPreferences`.
   */
  allModules: () => [...notificationPreferenceKeys.all, '__all_modules__'] as const,
};

// ── The all-modules read ───────────────────────────────────────

/** One module's slice of the bulk read, plus the shared load state. */
export interface BulkNotificationPreferences {
  byModule: Record<string, PreferencesResponse>;
  isLoading: boolean;
  /** `useQuery`'s own error type — widening it to `unknown` here would collapse
   *  the single-module mount's `Error | null` for every consumer. */
  error: Error | null;
}

/**
 * Published by the all-modules mount so every `ModuleNotificationBlock` under it
 * reads its slice of ONE request instead of fetching its own.
 *
 * `null` (no provider) is the single-module mount, which keeps its own per-module
 * query — that is one request for one block, and nothing to batch.
 *
 * Why this exists: `/settings?tab=notifications` renders a block per catalog
 * module (23 of them), and each block's own query was a separate HTTP GET holding
 * a request-pool connection for its whole duration. Two users on the page
 * demanded ~44 connections from a pool whose ceiling is 15, exhausting it —
 * production alarm `db-pool-request-timeouts`, 2026-08-21.
 */
export const BulkNotificationPreferencesContext =
  createContext<BulkNotificationPreferences | null>(null);

/** Fetch every catalog module's matrix in one request. Mount ONCE, per page. */
export function useAllNotificationPreferences(): BulkNotificationPreferences {
  const query = useQuery({
    queryKey: notificationPreferenceKeys.allModules(),
    queryFn: ({ signal }) => getAllPreferencesApiV1NotificationPreferencesGet(signal),
  });

  // Memoized: this is a context value, so a fresh identity per render would
  // re-render every block below it on every parent render.
  return useMemo(
    () => ({
      byModule: query.data?.modules ?? {},
      isLoading: query.isLoading,
      error: query.error,
    }),
    [query.data, query.isLoading, query.error],
  );
}

// ── Hook ───────────────────────────────────────────────────────

export function useNotificationPreferences(module: string) {
  const queryClient = useQueryClient();
  const bulk = useContext(BulkNotificationPreferencesContext);

  const query = useQuery({
    queryKey: notificationPreferenceKeys.module(module),
    queryFn: ({ signal }) =>
      getPreferencesApiV1NotificationPreferencesModuleGet(module, signal),
    // Under the all-modules mount this module's data is already in hand, so the
    // per-module request is exactly the fan-out we are here to prevent.
    enabled: !!module && bulk === null,
  });

  const data = bulk ? bulk.byModule[module] : query.data;
  const isLoading = bulk ? bulk.isLoading : query.isLoading;
  const error = bulk ? bulk.error : query.error;

  // Invalidate by PREFIX rather than by whichever key this block happens to read:
  // the two reads serve the same rows, so a write to one has to refresh the
  // other. React Query only refetches ACTIVE queries, so this costs one request
  // either way — the bulk read under the all-modules mount, the per-module read
  // under a single mount (the other is disabled and stays untouched).
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.all });
  };

  const updateEventsMutation = useMutation({
    mutationFn: (events: EventPreferenceUpdate[]) =>
      updateEventPreferencesApiV1NotificationPreferencesModuleEventsPut(module, { events }),
    onSuccess: invalidate,
  });

  const updateModuleMutation = useMutation({
    mutationFn: (data: ModulePreferenceUpdate) =>
      updateModulePreferencesApiV1NotificationPreferencesModuleModulePut(module, data),
    onSuccess: invalidate,
  });

  const resetMutation = useMutation({
    mutationFn: () => resetPreferencesApiV1NotificationPreferencesModuleResetPost(module),
    onSuccess: invalidate,
  });

  return {
    preferences: data,
    categories: data?.categories ?? [],
    modulePreferences: data?.module_preferences,
    isLoading,
    error,

    updateEventPreferences: updateEventsMutation.mutate,
    updateModulePreferences: updateModuleMutation.mutate,
    resetPreferences: resetMutation.mutate,

    isUpdating:
      updateEventsMutation.isPending ||
      updateModuleMutation.isPending ||
      resetMutation.isPending,
  };
}
